import {h} from 'preact';
import {useState, useRef, useEffect} from 'preact/hooks';
import Panel from 'utils/panel';
import { formatMoney } from 'utils/storefront/format-resource';
import Storefront from 'utils/storefront';
import Icon from "utils/icon";
import updateURL from 'utils/update-url';
import { productGet } from '../utils/storefront/queries';
import { withCartContext } from './cart-context';
import CartItem from './cart-item';
import Recommendations from './recommendations';
import ShopPay from './shop-pay';

const Cart = ({
	cart, 
	emptyCartCopy, 
	emptyCartCtaUrl, 
	emptyCartCtaText, 
	recommendations,
	isB2B 
}) => {
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get(
    'cart',
  );
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector('[data-header-cart-trigger]'),
    closeTriggerEl: null,
  });

  const swatchesInCart = 
  cart?.state?.items?.some(
    item => item.product_type.toLowerCase() === 'swatch'
  )
  const storefront = Storefront.getInstance();
  const [checkIfAllVariantsAreAvailable, setAllVariantsAreAvailable] = 
  useState(true)

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
  }, [closeTriggerEl]);

  useEffect(() => {
    const liquidCartPrice = document.querySelector('[data-cart-subtotal]');
    if(cart.state.total_price){
      liquidCartPrice.textContent = formatMoney({
        amount: cart.state.total_price / 100,
        currencyCode: window.TMW?.localization?.currencyCode,
      })
    } 
  }, [cart])
  
  const handleCheckoutClick = () => {
    window.location = updateURL('/checkout');
  };

  const findVariantById = (variants, id) => {
    const gid = `gid://shopify/ProductVariant/${id}`
    return variants?.find(variant => variant.node.id === gid)?.node || null;
  };

  const getLineProductData = async (line) => {
    const lineProductGid = `gid://shopify/Product/${line.product_id}`
    const response = await storefront.request({
      query: productGet,
      variables: {
        id: lineProductGid
      },
    });

    if (!response.data) {
      // eslint-disable-next-line no-console
      console.error(
        'cart-item.js: Storefront API request failed',
        response,
      );
      return [];
    }

    return response.data;
  };

  useEffect(() => {
    const fetchData = async (line) => {
      try {
        const lineProductData = await getLineProductData(line);
        const variantData = findVariantById(
          lineProductData.product?.variants?.edges,
          line.id
        );

        return {variantData, line}
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('cart-item.js: Storefront API request failed', error);
      }
      return null;
    };

    if (!cart?.state?.items) return

    const promises = cart?.state?.items.map(item => fetchData(item))
    Promise.all(promises)
      .then(results => {
        let isAvailable = true
        results.forEach(({variantData, line}) => {
          if (variantData?.continueSellingOnlyForB2b?.value === 'true' && !isB2B && line.quantity > variantData.quantityAvailable) {
            isAvailable = false
          } else if (!isB2B && line.quantity > variantData.quantityAvailable && variantData?.continueSellingOnlyForB2b?.value !== 'false'){
            isAvailable = false
          }
        })
        setAllVariantsAreAvailable(isAvailable)
      })
      .catch(error => {
        // eslint-disable-next-line no-console
        console.error(error)
      })


  }, [cart?.state?.items, cart]);

  return (
    <Panel overlay={true} triggers={panelTriggers} defaultOpen={isCartRedirect} disableBodyScroll={true} context="cart">
      <div className="h-[100dvh]" data-panel-focus-trap>
        <div className="sticky top-0 flex items-center justify-between px-3 md:px-5 py-5">
          <p className="utility-1-bold">
            {window.TMW.Strings.cart.title} {cart?.state?.item_count === 0
              ? null
              : `(${cart?.state?.item_count})`}
          </p>
          <button type="button" ref={closeTriggerEl} tabIndex="0">
            <Icon name="close"/>
          </button>
        </div>
        <div className="max-h-[calc(100dvh-222px)] overflow-auto">
          <div className="flex flex-col h-[calc(100dvh-222px)] divide-y md:divide-y-0">
            {
              !cart?.state?.items?.length ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-4 h-full">
                  {emptyCartCopy 
                    && <p className="body-1">{emptyCartCopy}</p>}
                  {emptyCartCtaUrl && <a href={emptyCartCtaUrl}>
                    <button
                      className="button button--primary-outline no-underline"
                      type="button"
                      tabIndex="0"
                    >
                      {emptyCartCtaText}
                    </button>
                  </a>}
                </div>
              ) : (
                <div className="flex-1 flex flex-col gap-3 md:gap-5">
                  {cart?.state?.items?.map((line) => (
                    line.product_type.toLowerCase() !== 'swatch' &&
                    <CartItem 
                      key={line.id} 
                      line={line} 
                      isB2B={isB2B} 
                    /> 
                  ))}
                  {swatchesInCart && 
                    <div className="border-t border-primary">
                      <div className="flex flex-col gap-1 p-3 pb-0 py-5.5 items-start 
                        md:p-5 md:pb-0 md:flex-row md:items-center md:gap-2"
                      >
                        <p className="text-primary utility-1-bold uppercase">
                          {window.TMW.Strings.cart.yourFabricSwatches}
                        </p>
                        <p className="text-secondary utility-2-bold uppercase">
                          {window.TMW.Strings.cart.swatchLimit}
                        </p>
                      </div>
                      {cart?.state?.items?.map((line) => (
                        line.product_type.toLowerCase() === 'swatch' &&
                        <CartItem key={line.id} line={line} />
                      ))}
                    </div>
                  }
                </div>
              )
            }
            <Recommendations {...recommendations} />
          </div>
        </div>
        <div className="sticky bottom-0 flex flex-col gap-1 border-t border-black p-3 md:p-5">
          <div className="flex justify-between utility-2 uppercase">
            <span>{window.TMW.Strings.cart.summary.subtotal}</span>
            {cart?.state?.total_price && (
              <span>{formatMoney({
                amount: cart.state.total_price / 100,
                currencyCode: window.TMW?.localization?.currencyCode,
              })}</span>
            )}
          </div>
          <div className="flex justify-between utility-2 uppercase">
            <span dangerouslySetInnerHTML={{
              __html: window.TMW.Strings.cart.summary.shippingTaxTitle
            }}/>
            <span>{window.TMW.Strings.cart.summary.shippingTaxValue}</span>
          </div>

          <div className="flex justify-between utility-1-bold uppercase">
            <span>{window.TMW.Strings.cart.summary.estimatedTotal}</span>
            {cart?.state?.total_price && (
              <span>{formatMoney({
                amount: cart.state.total_price / 100,
                currencyCode: window.TMW?.localization?.currencyCode,
              })}</span>
            )}
          </div>
          {cart?.state?.total_price > 0 && <ShopPay/>}
          <button
            className="justify-center w-full no-underline button button--primary"
            type="button"
            onClick={handleCheckoutClick}
            disabled={!checkIfAllVariantsAreAvailable}
          >
            {window.TMW.Strings.cart.summary.checkout}
          </button>
        </div>
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);